import {
  GetStudentNotes,
  AddStudentNote,
  UpdateStudentNote,
  RemoveStudentNote,
} from "../../DriveScout/API/models/StudentNotesModel";

const DEFAULT_PAGE_BY = 5;

/**
 * The single copy of a student's notes.
 *
 * The admin profile page and the dedicated notes screen both render this slice
 * live, so a note added on one is visible on the other without a page refresh
 * (SUP-1408). Every mutating action refetches from the server rather than
 * patching the array locally, because the list is sorted newest-first and
 * paginated -- a local push lands in the wrong place and leaves `total` stale.
 *
 * Field names match what the Angular templates already bind to.
 */
const state = {
  studentId: null,
  data: [],
  total: 0,
  currentPage: 0,
  page_by: DEFAULT_PAGE_BY,
  loading: false,
};

const mutations = {
  /**
   * Clears the list when the student changes so a profile never briefly
   * renders the previously-viewed student's notes.
   */
  SET_STUDENT(state, studentId) {
    if (state.studentId === studentId) {
      return;
    }
    state.studentId = studentId;
    state.data = [];
    state.total = 0;
    state.currentPage = 0;
  },
  SET_NOTES(state, notes) {
    state.data = notes.data;
    state.total = notes.total;
    state.currentPage = notes.current_page;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
};

const actions = {
  SET_STUDENT({ commit }, studentId) {
    commit("SET_STUDENT", studentId);
  },

  GET_STUDENT_NOTES({ commit }, { studentId, page }) {
    commit("SET_STUDENT", studentId);
    commit("SET_LOADING", true);
    return GetStudentNotes(studentId, { page })
      .then((response) => {
        const notes = response.data.notes;
        commit("SET_NOTES", notes);
        return notes;
      })
      .finally(() => {
        commit("SET_LOADING", false);
      });
  },

  /**
   * Page 1 is where the new note lands -- the API returns notes newest-first.
   */
  ADD_STUDENT_NOTE({ dispatch }, { studentId, note }) {
    return AddStudentNote(studentId, note).then((response) => {
      return dispatch("GET_STUDENT_NOTES", { studentId, page: 1 }).then(() => {
        return response.data;
      });
    });
  },

  UPDATE_STUDENT_NOTE({ dispatch, state }, { studentId, noteId, note }) {
    return UpdateStudentNote(studentId, noteId, note).then((response) => {
      return dispatch("GET_STUDENT_NOTES", {
        studentId,
        page: state.currentPage,
      }).then(() => {
        return response.data;
      });
    });
  },

  REMOVE_STUDENT_NOTE({ dispatch, state }, { studentId, noteId }) {
    return RemoveStudentNote(studentId, noteId).then((response) => {
      const page = state.currentPage;
      return dispatch("GET_STUDENT_NOTES", { studentId, page }).then(
        (notes) => {
          // Deleting the only note on the last page leaves that page empty --
          // step back so the admin isn't stranded on a blank list.
          if (notes.data.length === 0 && page > 1) {
            return dispatch("GET_STUDENT_NOTES", {
              studentId,
              page: page - 1,
            }).then(() => response.data);
          }
          return response.data;
        },
      );
    });
  },
};

const getters = {
  studentNotes: (state) => state.data,
  studentNotesTotal: (state) => state.total,
  studentNotesLoading: (state) => state.loading,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
